/**
 * PATH: apps/qa/src/pages/ai-insights/ReadinessCheckResults.tsx
 * PURPOSE: Display pack readiness check results with score, issues, recommendations
 */

import { cn } from '@/lib/utils'
import type { ReadinessCheck } from './types'
import {
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Loader2,
  ClipboardCheck,
  ChevronRight,
  ArrowRight,
} from 'lucide-react'

export function ReadinessCheckResults({
  data,
  isLoading,
  error,
}: {
  data?: ReadinessCheck
  isLoading: boolean
  error: Error | null
}) {
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/30 animate-pulse" />
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="mt-4 text-foreground font-medium">Checking readiness...</p>
        <p className="text-sm text-muted-foreground">Verifying pack completeness</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-destructive/50 p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        <p className="font-medium text-foreground">Check Failed</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message || 'Please try again'}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border border-dashed p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <ClipboardCheck className="w-8 h-8 text-muted-foreground" />
        </div>
        <p className="font-medium text-foreground">No Readiness Check Yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Select an inspection and click "Run Analysis" to check pack readiness
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Score Card */}
      <div className={cn(
        'rounded-xl border p-6',
        data.ready
          ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800'
          : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold',
              data.ready ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
            )}>
              {data.score}
            </div>
            <div>
              <h3 className={cn(
                'font-bold text-lg',
                data.ready ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
              )}>
                {data.ready ? '✓ Ready for Pack Generation' : '⚠ Not Ready Yet'}
              </h3>
              <p className={cn(
                'text-sm',
                data.ready ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'
              )}>
                {data.issues.length === 0
                  ? 'All checks passed! You can generate the QA pack.'
                  : `${data.issues.length} issue${data.issues.length === 1 ? '' : 's'} need attention`}
              </p>
            </div>
          </div>
          {data.ready && (
            <button className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2">
              Generate Pack
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Issues */}
      {data.issues.length > 0 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">Issues to Resolve</h3>
          </div>
          <div className="divide-y divide-border">
            {data.issues.map((issue, index) => (
              <div key={index} className="p-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'p-1.5 rounded-lg flex-shrink-0',
                    issue.severity === 'high' && 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
                    issue.severity === 'medium' && 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
                    issue.severity === 'low' && 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  )}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{issue.description}</p>
                    <p className="text-sm text-muted-foreground mt-1 capitalize">
                      Category: {issue.category.replace(/_/g, ' ')}
                    </p>
                    {issue.fix_action && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
                        <ChevronRight className="w-4 h-4" />
                        <span>{issue.fix_action}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            Recommendations
          </h3>
          <ul className="space-y-2">
            {data.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
